import type { LabelInValue } from 'models';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Form, FormActions, Input, Modal, MultiValueInput, message } from 'ui';
import { useInviteUsersMutation } from '../../../queries/mutations/useInviteUsersMutation';

type P = {
  community?: LabelInValue;
  isOpened: boolean;
  onCancel: () => void;
};

type SmsInviteFormValues = {
  communityId: string;
  phones: string[];
};

const SmsInviteModal = ({ community, isOpened, onCancel }: P) => {
  const { isPending: isInvitingUsers, mutate: inviteUsers } = useInviteUsersMutation();
  const { t } = useTranslation();

  const onFinish = (data: SmsInviteFormValues) => {
    inviteUsers(
      {
        id: data.communityId,
        invitees: {
          emails: [],
          phones: data.phones,
          userIds: [],
        },
      },
      {
        onSuccess() {
          message.success('Invitations sent successfully');
          onCancel();
        },
      },
    );
  };

  const initialValues = {
    communityId: community?.value,
  };

  return (
    <Modal isOpened={isOpened} onCancel={onCancel} title="Invite via SMS">
      <Modal.HugContent>
        <Form<SmsInviteFormValues> initialValues={initialValues} onFinish={onFinish}>
          <Form.Item<SmsInviteFormValues> hidden name="communityId" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item<SmsInviteFormValues>
            extra="Type phone numbers and press enter, space or comma - Or paste from a spreadsheet!"
            label="Phone numbers"
            name="phones"
            rules={[
              {
                // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                async validator(_, value) {
                  if (Array.isArray(value)) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                    const invalidNumbers = value.filter((phone) => !isValidPhoneNumber(phone));
                    if (invalidNumbers.length > 0) {
                      throw new Error(
                        t(
                          'Error: Some phone numbers are invalid. The number must start with area code + and must not contain any spaces. Example: +12133734253',
                        ),
                      );
                    }
                  }
                },
              },
              {
                required: true,
              },
            ]}
          >
            <MultiValueInput allowInvalidValues validateValue={(phoneNumber) => isValidPhoneNumber(phoneNumber)} />
          </Form.Item>

          <Modal.HugContent.Footer>
            <FormActions
              right={{
                children: 'Back',
                onClick() {
                  onCancel();
                },
              }}
              submit={{
                children: 'Send invitations',
                loading: isInvitingUsers,
              }}
            />
          </Modal.HugContent.Footer>
        </Form>
      </Modal.HugContent>
    </Modal>
  );
};

export default SmsInviteModal;
